import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"

const SESSION_HEADER = "X-Verification-Session"

const messageOf = (error, fallback) =>
  error.response?.data?.error?.errors?.[0]?.message
  || error.response?.data?.message
  || fallback

const sessionHeaders = (sessionToken) => ({ headers: { [SESSION_HEADER]: sessionToken } })

// The enrollment funnel proves the card with a session token; the CRM is a signed-in staff member
// naming the card by id. Same three actions, two doors, one slice.
// The CRM's half is mounted outside the /card-verification prefix on the API: that prefix is a
// declared contextless surface for the session-less enrolment funnel, and an authenticated route
// under it would run with tenant scoping suspended.
const route = (sessionToken, path) => (sessionToken ? `/card-verification${path}` : `/cards/verification${path}`)
const config = (sessionToken) => (sessionToken ? sessionHeaders(sessionToken) : {})

export const startCardVerification = createAsyncThunk(
  'cardVerification/start',
  async ({ sessionToken, affiliateId, billingAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(
        route(sessionToken, '/start'),
        { affiliateId, billingAccountId },
        config(sessionToken)
      )
      return response.data.data
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        message: messageOf(error, 'Could not start card verification'),
      })
    }
  }
)

export const recordCardVerificationAnswers = createAsyncThunk(
  'cardVerification/recordAnswers',
  async ({ sessionToken, verificationId, clientSeesDebit, enteredAmountCents, affiliateVerdict, billingAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(
        route(sessionToken, `/${verificationId}/answers`),
        { clientSeesDebit, enteredAmountCents, affiliateVerdict, billingAccountId },
        config(sessionToken)
      )
      return response.data.data
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        message: messageOf(error, 'Could not record the verification result'),
      })
    }
  }
)

// Read-only by design: this is what a page refresh calls, and it must never be able to start a
// second real charge.
export const getCardVerificationStatus = createAsyncThunk(
  'cardVerification/status',
  async ({ sessionToken, billingAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get(
        route(sessionToken, '/status'),
        sessionToken ? sessionHeaders(sessionToken) : { params: { billingAccountId } }
      )
      return response.data.data
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        message: messageOf(error, 'Could not read the verification status'),
      })
    }
  }
)

export const releaseCardVerification = createAsyncThunk(
  'cardVerification/release',
  async ({ verificationId, billingAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(
        `/cards/verification/${verificationId}/release`,
        { billingAccountId }
      )
      return response.data.data
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        message: messageOf(error, 'Could not release the verification charge'),
      })
    }
  }
)

const initialState = {
  session: null,
  verification: null,
  loading: false,
  error: null,
}

const cardVerificationSlice = createSlice({
  name: 'cardVerification',
  initialState,
  reducers: {
    verificationSessionReceived: (state, action) => {
      state.session = action.payload
      state.verification = null
      state.error = null
    },
    cardVerificationReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(startCardVerification.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(startCardVerification.fulfilled, (state, action) => {
        state.loading = false
        state.verification = action.payload
      })
      .addCase(startCardVerification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(recordCardVerificationAnswers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(recordCardVerificationAnswers.fulfilled, (state, action) => {
        state.loading = false
        state.verification = action.payload
      })
      .addCase(recordCardVerificationAnswers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(getCardVerificationStatus.fulfilled, (state, action) => {
        state.verification = action.payload
      })
      .addCase(releaseCardVerification.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(releaseCardVerification.fulfilled, (state, action) => {
        state.loading = false
        state.verification = action.payload
      })
      .addCase(releaseCardVerification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { verificationSessionReceived, cardVerificationReset } = cardVerificationSlice.actions

export const selectCardVerification = (state) => state.cardVerification.verification
export const selectCardVerificationSession = (state) => state.cardVerification.session
export const selectCardVerificationLoading = (state) => state.cardVerification.loading
export const selectCardVerificationError = (state) => state.cardVerification.error

export const isCardVerificationPassed = (verification) => verification?.verdict === 'passed'

export default cardVerificationSlice.reducer
